export type FileAssetName = string;
export type InlineAssetHashName = string;

export type AssetName = FileAssetName | InlineAssetHashName;

export type AssetSrc = string;

export enum EAssetContentType {
  Script,
  Styles,
}

export enum EAssetType {
  File,
  Inline,
}

export type AssetProps = Record<string, boolean | string>;

/**
 * Asset - это исполняемый и загружаемый ресурс, необходимый для работы Bundle
 */
export interface IAsset<Type extends EAssetType, ContentType extends EAssetContentType = EAssetContentType> {
  /**
   * Тип ассета
   */
  readonly type: Type;
  /**
   * Тип контента
   */
  readonly contentType: ContentType;
  /**
   * Атрибуты DOM-элемента
   */
  readonly props?: AssetProps;
  /**
   * @todo
   * Тип загрузки ассета:
   * - esm - ES Modules
   * - dom - Вставка через DOM
   *
   * loadType: 'esm' | 'dom';
   */
  /**
   * @todo
   * Время существования ассета:
   * - Singletone
   * - Transient
   * - Scoped
   * Есть вероятность, что некоторые ассеты необходимо вставлять
   * каждый раз на страницу для того, чтобы заново запускался код.
   */
}

/**
 * Загружаемый ресурс (файл)
 */
export interface IFileAsset<ContentType extends EAssetContentType = EAssetContentType>
  extends IAsset<EAssetType.File, ContentType> {
  /**
   * Полный путь до ассета
   */
  readonly src: AssetSrc;
}

/**
 * Ассеты, которые представляют собой исполняемые модули, у которых могут быть
 * экспортируемые инетрфейсы. По своей сути это те же ES модули
 */
export interface IModuleAsset extends IFileAsset<EAssetContentType.Script> {
  /**
   * Есть ли в модуле экспорты
   */
  hasExports: boolean;
}

/**
 * Ресурс, который не требует предварительной загрузки и встраивается в страницу
 * в виде исходного кода
 */
export interface IInlineAsset<ContentType extends EAssetContentType = EAssetContentType>
  extends IAsset<EAssetType.Inline, ContentType> {
  /**
   * Исходный код ресурса
   */
  content: string;
  /**
   * Уникальное имя ресурса
   */
  hash: InlineAssetHashName;
}

export type ScriptAssetProps = AssetProps & {
  /**
   * Аттрибут async для тега script
   *
   * По умолчанию true
   */
  async?: boolean;
  /**
   * Аттрибут defer для тега script
   *
   * По умолчанию отсутствует
   */
  defer?: boolean;
  /**
   * Аттрибут crossorigin для тега script
   *
   * По умолчанию true
   */
  crossorigin?: boolean | 'anonymous' | 'use-credentials' | '';
};

export function isFileAsset(asset: IAsset<EAssetType>): asset is IFileAsset {
  return asset.type === EAssetType.File;
}

export function isModuleAsset(asset: IAsset<EAssetType>): asset is IModuleAsset {
  return asset.type === EAssetType.File && 'hasExports' in asset;
}

/**
 * Сокращения в имени атрибута сделаны для уменьшения размера ответа
 *
 * data-cian-asset-hash="content-hash"
 *
 * C - Cian
 * A - Asset
 * H - Hash
 */
export const INLINE_ASSET_ATTR_HASH_NAME = 'cah';
